import React, { useEffect, useMemo, useRef, useState } from 'react'
import { IoIosArrowRoundBack } from 'react-icons/io'
import { useHistory } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { FiUploadCloud } from 'react-icons/fi'
import { useLocations } from '../../hooks/useLocations'
import api, { fetcher } from '../../utils/api'
import { ErrorNotification, SuccessNotification } from '../../utils'
import useSWR from 'swr'
import Select from 'react-select'
import CreatableSelect from 'react-select/creatable'
import {
  getMainCategories,
  getSubcategoriesForMainIds,
  pruneSubcategoryIds,
} from '../../utils/productCategories'
import { trimFormValue } from '../../utils/formValues'

const trimField = { setValueAs: trimFormValue }

const MAX_IMAGES = 5
const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png']

const AddService = () => {
  const { regionNames } = useLocations()
  const history = useHistory()
  const [images, setImages] = useState(Array(MAX_IMAGES).fill(null))
  const [imageFiles, setImageFiles] = useState(Array(MAX_IMAGES).fill(null))
  const fileInputs = useRef([])
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const stored = localStorage.getItem('theme')
    if (stored) return stored === 'dark'
    localStorage.setItem('theme', 'dark')
    return true
  })

  // Fetch all required data
  const { data: categories } = useSWR(`service-orders/products/categories`, fetcher)

  const { register, handleSubmit, setValue, watch, formState: { errors, isSubmitting } } = useForm({
    defaultValues: {
      name: '',
      description: '',
      country: '',
      region: '',
      community: '',
      main_category_ids: [],
      subcategory_ids: [],
      is_negotiable: false,
      unit_price: '',
      delivery_countries: [],
      delivery_regions: [],
      delivery_communities: [],
      visible: true,
    }
  })

  // Custom validation for main_category_ids
  const validateMainCategories = (value) => {
    if (!value || value.length === 0) {
      return 'At least one main category is required'
    }
    return true
  }

  const validateImages = () => {
    const hasImages = imageFiles.some(file => file instanceof File)
    if (!hasImages) {
      return 'At least one service image is required'
    }

    const imageCount = imageFiles.filter(file => file instanceof File).length
    if (imageCount < 2) {
      return 'At least 2 service images are required'
    }

    return true
  }

  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Only JPG, JPEG, and PNG files are allowed'
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File size must be less than 5MB'
    }
    return null
  }

  const handleImageChange = (idx, e) => {
    const file = e.target.files[0]
    if (file) {
      const validationError = validateFile(file)
      if (validationError) {
        ErrorNotification(validationError)
        return
      }

      const reader = new FileReader()
      reader.onloadend = () => {
        // Update preview images
        setImages(prev => {
          const newImages = [...prev]
          newImages[idx] = reader.result
          return newImages
        })

        // Update file objects
        setImageFiles(prev => {
          const newFiles = [...prev]
          newFiles[idx] = file
          return newFiles
        })
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveImage = (idx) => {
    setImages(prev => {
      const newImages = [...prev]
      newImages[idx] = null
      return newImages
    })

    setImageFiles(prev => {
      const newFiles = [...prev]
      newFiles[idx] = null
      return newFiles
    })

    if (fileInputs.current[idx]) {
      fileInputs.current[idx].value = ''
    }
  }

  const watchedValues = watch()

  const mainCategoryOptions = useMemo(
    () => getMainCategories(categories).map((category) => ({
      value: category.id,
      label: category.name,
    })),
    [categories]
  )

  const availableSubcategoryOptions = useMemo(
    () => getSubcategoriesForMainIds(categories, watchedValues.main_category_ids).map((category) => ({
      value: category.id,
      label: category.name,
    })),
    [categories, watchedValues.main_category_ids]
  )

  const handleMainCategoryChange = (selectedOptions) => {
    const values = selectedOptions ? selectedOptions.map((option) => option.value) : []
    setValue('main_category_ids', values)
    setValue(
      'subcategory_ids',
      pruneSubcategoryIds(categories, values, watchedValues.subcategory_ids)
    )
  }

  const onSubmit = async (data) => {
    try {
      // Validate images before submission
      const imageValidation = validateImages()
      if (imageValidation !== true) {
        ErrorNotification(imageValidation)
        return
      }

      // Validate main categories
      const categoryValidation = validateMainCategories(data.main_category_ids)
      if (categoryValidation !== true) {
        ErrorNotification(categoryValidation)
        return
      }

      // Create the service first with JSON payload
      const payload = {
        name: data.name,
        description: data.description,
        item_type: "service",
        country: data.country,
        region: data.region,
        community: data.community,
        main_category_ids: data.main_category_ids,
        subcategory_ids: data.subcategory_ids,
        is_negotiable: data.is_negotiable,
        unit_price: parseFloat(data.unit_price).toFixed(2),
        delivery_countries: data.delivery_countries,
        delivery_regions: data.delivery_regions,
        delivery_communities: data.delivery_communities,
        is_active: data.visible
      }

      const response = await api.post('service-orders/products/', payload)

      // If there are images, upload them separately
      if (imageFiles.some(file => file instanceof File)) {
        const formData = new FormData()

        // Handle image files
        imageFiles.forEach((file, index) => {
          if (file instanceof File) {
            formData.append('uploaded_images', file)
            formData.append('quantity', 1)
            formData.append('unit_price', parseFloat(data.unit_price).toFixed(2))

          }
        })

        const imageResponse = await api.patch(`service-orders/products/${response.data.id}/`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
      }

      if (response.status === 201) {
        SuccessNotification("Service added successfully")
        history.goBack()
      }
    } catch (error) {
      ErrorNotification(error.response?.data?.message || "An error occurred")
    }
  }

  // React Select custom styles
  const darkSelectStyles = {
    container: (provided) => ({
      ...provided,
      width: '100%',
    }),
    control: (provided, state) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#2e2e2e' : '#fff',
      borderColor: state.isFocused ? '#2e2e2e' : '#2e2e2e',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(251, 191, 36, 0.2)' : 'none',
      '&:hover': {
        borderColor: '#fbbf24'
      }
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#1f2937'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#fbbf24' : state.isFocused ? '#374151' : '#1f2937',
      color: state.isSelected ? 'white' : '#d1d5db',
      '&:hover': {
        backgroundColor: state.isSelected ? '#fbbf24' : '#374151'
      }
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#d1d5db'
    }),
    input: (provided) => ({
      ...provided,
      color: '#d1d5db'
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#fef3c7'
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#92400e'
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#92400e',
      '&:hover': {
        backgroundColor: '#fbbf24',
        color: 'white'
      }
    })
  }

  useEffect(() => {
    const stored = localStorage.getItem('theme')
    if (stored) setIsDarkMode(stored === 'dark')

    // listen for theme change
    const handleThemeChange = () => {
      const stored = localStorage.getItem('theme')
      if (stored) setIsDarkMode(stored === 'dark')
    }
    window.addEventListener('themeChange', handleThemeChange)
    return () => window.removeEventListener('themeChange', handleThemeChange)
  }, [])

  return (
    <div className="w-full lg:px-10 px-2 h-full py-6 lg:py-10">
      <div className="w-full">
        <button onClick={() => history.goBack()} className="mb-2 flex flex-row items-center">
          <IoIosArrowRoundBack size={25} className="mr-2 dark:text-white" />
          <p className="text-sm font-light capitalize dark:text-white">Back</p>
        </button>

        <h2 className="text-2xl font-bold mb-4 dark:text-white">Add New Service</h2>

        <form className='bg-white dark:bg-dark rounded-lg overflow-hidden' onSubmit={handleSubmit(onSubmit)}>
          <div className="px-3 py-4 md:px-8 md:py-8 overflow-x-hidden">

            {/* Service Details Section */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-yellowcolor">Service Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Service Name */}
                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-white">Service Name<span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    placeholder="Enter service name"
                    maxLength={100}
                    className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                    {...register('name', {
                      ...trimField,
                      required: 'Service name is required',
                      minLength: { value: 3, message: 'Service name must be at least 3 characters long' },
                      maxLength: { value: 100, message: 'Service name cannot exceed 100 characters' }
                    })}
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
                </div>

                {/* Country */}
                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-white">Select Country<span className="text-red-500">*</span></label>
                  <select
                    className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent"
                    {...register('country', { required: 'Country is required' })}
                  >
                    <option value="">Select country</option>
                    {/* {countries?.map(opt => <option key={opt?.name} value={opt?.name}>{opt?.name}</option>)} */}
                    <option key="Ghana" value="Ghana">Ghana</option>
                  </select>
                  {errors.country && <p className="text-red-500 text-xs mt-1">{errors.country.message}</p>}
                </div>

                {/* Region/State */}
                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-white">Region/State<span className="text-red-500">*</span></label>
                  <select
                    className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent"
                    {...register('region', { required: 'Region/State is required' })}
                  >
                    <option value="">Select region</option>
                    {regionNames.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                  {errors.region && <p className="text-red-500 text-xs mt-1">{errors.region.message}</p>}
                </div>

                {/* Town/Community */}
                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-white">Town/Community<span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    placeholder="Enter your community"
                    maxLength={100}
                    className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                    {...register('community', {
                      ...trimField,
                      required: 'Town/Community is required',
                      minLength: { value: 2, message: 'Community name must be at least 2 characters long' },
                      maxLength: { value: 100, message: 'Community name cannot exceed 100 characters' }
                    })}
                  />
                  {errors.community && <p className="text-red-500 text-xs mt-1">{errors.community.message}</p>}
                </div>

                {/* Description - Full Width */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 dark:text-white">Service Description<span className="text-red-500">*</span></label>
                  <textarea
                    placeholder='Describe this service in detail'
                    cols={30}
                    rows={6}
                    className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                    {...register('description', {
                      ...trimField,
                      required: 'Service description is required',
                    })}
                  ></textarea>
                  <div className="flex justify-between items-center mt-1">
                    {errors.description && <p className="text-red-500 text-xs">{errors.description.message}</p>}
                  </div>
                </div>

                {/* Service Price */}
                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-white">Service Price (GHC)<span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    placeholder="eg. 200"
                    min={1}
                    max={999999}
                    step="0.01"
                    className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                    {...register('unit_price', {
                      required: 'Service price is required',
                      min: { value: 1, message: 'Price must be at least GHC1' },
                      max: { value: 999999, message: 'Price cannot exceed GHC999,999' }
                    })}
                  />
                  {errors.unit_price && <p className="text-red-500 text-xs mt-1">{errors.unit_price.message}</p>}
                </div>

                {/* Open for Negotiation */}
                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-white">Open for Negotiation</label>
                  <div className="flex flex-col sm:flex-row sm:items-center items-start gap-2 sm:gap-3">
                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        {...register('is_negotiable')}
                        className="sr-only peer"
                        aria-label="Toggle price negotiation"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-400"></div>
                    </label>
                    <span className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">This informs clients that the service price is negotiable</span>
                  </div>
                </div>

              </div>
            </div>

            <hr className='my-8 border-light-white dark:border-border-dark' />

            {/* Service Categories Section */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-yellowcolor">Service Categories</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Main Categories */}
                <div className="w-full min-w-0">
                  <label className="block text-sm font-medium mb-1 dark:text-white">Main Categories<span className="text-red-500">*</span></label>
                  <Select
                    isMulti
                    options={mainCategoryOptions}
                    value={watchedValues.main_category_ids?.map((id) => ({
                      value: id,
                      label: categories?.find((cat) => cat.id === id)?.name,
                    }))}
                    onChange={handleMainCategoryChange}
                    styles={darkSelectStyles}
                    placeholder="Select main categories..."
                    className="dark:text-white"
                  />
                  {errors.main_category_ids && <p className="text-red-500 text-xs mt-1">{errors.main_category_ids.message}</p>}
                </div>

                {/* Sub Categories */}
                <div className="w-full min-w-0">
                  <label className="block text-sm font-medium mb-1 dark:text-white">Sub Categories</label>
                  <Select
                    isMulti
                    isDisabled={!watchedValues.main_category_ids?.length}
                    options={availableSubcategoryOptions}
                    value={watchedValues.subcategory_ids
                      ?.map((id) => ({
                        value: id,
                        label: categories?.find((cat) => cat.id === id)?.name,
                      }))
                      .filter((option) => option.label)}
                    onChange={(selectedOptions) => {
                      const values = selectedOptions ? selectedOptions.map((option) => option.value) : []
                      setValue('subcategory_ids', values)
                    }}
                    styles={darkSelectStyles}
                    placeholder={
                      watchedValues.main_category_ids?.length
                        ? 'Select sub categories...'
                        : 'Select main categories first...'
                    }
                    className="dark:text-white"
                  />
                </div>

              </div>
            </div>

            <hr className='my-8 border-light-white dark:border-border-dark' />

            {/* Delivery Locations Section */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold mb-4 text-yellowcolor">Service Delivery Locations</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Delivery Country */}
                <div className="w-full min-w-0">
                  <label className="block text-sm font-medium mb-1 dark:text-white">Select Countries<span className="text-red-500">*</span></label>
                  <Select
                    isMulti
                    // options={countries?.map(country => ({ value: country.name, label: country.name }))}
                    options={[{ value: 'Ghana', label: 'Ghana' }]}
                    value={watchedValues.delivery_countries?.map(country => ({ value: 'Ghana', label: 'Ghana' }))}
                    onChange={(selectedOptions) => {
                      const values = selectedOptions ? selectedOptions.map(option => option.value) : []
                      setValue('delivery_countries', values)
                    }}
                    styles={darkSelectStyles}
                    placeholder="Select countries..."
                    className="dark:text-white"
                  />
                  {errors.delivery_countries && <p className="text-red-500 text-xs mt-1">{errors.delivery_countries.message}</p>}
                </div>

                {/* Delivery Region */}
                <div className="w-full min-w-0">
                  <label className="block text-sm font-medium mb-1 dark:text-white">Regions/States<span className="text-red-500">*</span></label>
                  <Select
                    isMulti
                    options={regionNames.map(region => ({ value: region, label: region }))}
                    value={watchedValues.delivery_regions?.map(region => ({ value: region, label: region }))}
                    onChange={(selectedOptions) => {
                      const values = selectedOptions ? selectedOptions.map(option => option.value) : []
                      setValue('delivery_regions', values)
                    }}
                    styles={darkSelectStyles}
                    placeholder="Select regions..."
                    className="dark:text-white"
                  />
                  {errors.delivery_regions && <p className="text-red-500 text-xs mt-1">{errors.delivery_regions.message}</p>}
                </div>

                {/* Delivery Community */}
                <div className="w-full min-w-0 md:col-span-2">
                  <label className="block text-sm font-medium mb-1 dark:text-white">Towns/Communities</label>
                  <CreatableSelect
                    isMulti
                    options={[]}
                    value={watchedValues.delivery_communities?.map(community => ({ value: community, label: community }))}
                    onChange={(selectedOptions) => {
                      const values = selectedOptions ? selectedOptions.map(option => option.value) : []
                      setValue('delivery_communities', values)
                    }}
                    styles={darkSelectStyles}
                    placeholder="Type and press Enter to add communities..."
                    className="dark:text-white"
                    noOptionsMessage={() => "Type to add a community"}
                    formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                    isValidNewOption={(inputValue) => inputValue.length >= 2}
                    createOptionPosition="first"
                  />
                  {errors.delivery_communities && <p className="text-red-500 text-xs mt-1">{errors.delivery_communities.message}</p>}
                </div>
              </div>
            </div>

            <hr className='my-8 border-light-white dark:border-border-dark' />

            {/* Service Images Section */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 text-yellowcolor">Service Images</h3>
              {/* Image Grid */}
              {images?.length > 0 && <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {images.map((img, idx) => (
                  <div key={idx} className="relative">
                    {img ? (
                      <div className="w-full aspect-square border border-gray-200 dark:border-border-dark rounded-lg overflow-hidden">
                        <img src={img} alt="preview" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(idx)}
                          className="absolute top-1 right-1 bg-white rounded-full w-6 h-6 flex items-center justify-center text-red-500 hover:bg-red-50 transition-colors"
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div className="w-full aspect-square border border-dashed border-gray-200 dark:border-border-dark rounded-lg flex items-center justify-center bg-gray-50 dark:bg-secondaryDark">
                        <button
                          type="button"
                          onClick={() => fileInputs.current[idx]?.click()}
                          className="text-gray-400 hover:text-yellowcolor transition-colors"
                        >
                          <FiUploadCloud size={24} />
                        </button>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          ref={el => fileInputs.current[idx] = el}
                          onChange={e => handleImageChange(idx, e)}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>}
            </div>

            {/* Visibility Toggle */}
            <div className='flex flex-col items-start sm:items-end justify-end w-full mb-6 mt-8'>
              <div className="flex flex-col">
                <span className="text-sm font-medium mb-1 dark:text-white">Visibility</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    {...register('visible')}
                    className="sr-only peer"
                    aria-label="Toggle service visibility"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-400"></div>
                </label>
              </div>
              <div className="text-xs text-gray-500 mt-2">Toggling visibility on makes your service visible on marketplace</div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col-reverse sm:flex-row border-t border-gray-200 dark:border-border-dark p-4 gap-3 sm:justify-end">
            <button
              type="button"
              onClick={() => history.goBack()}
              className="w-full sm:w-auto px-6 text-sm py-2.5 bg-white dark:bg-dark border dark:border-border-dark dark:text-white text-black rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`w-full sm:w-auto px-6 text-sm py-2.5 ${isSubmitting ? 'bg-gray-200' : 'bg-yellowcolor'} text-black rounded-lg font-semibold hover:bg-yellow-400 flex items-center justify-center gap-2`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : "Add New Service"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddService
